import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getPreferredRegions, savePreferredRegions } from './api';
import { loadFavoriteRegions, saveFavoriteRegions } from './regions';

const PREFERRED_REGIONS_QUERY_KEY = ['preferred-regions'] as const;

export interface UsePreferredRegions {
  /** 주로 가는 지역 목록 (서버 동기화, 실패 시 localStorage 캐시). */
  regions: string[];
  /** 지역 하나를 등록/해제 토글 (낙관적 갱신 + 서버 best-effort). */
  toggle: (name: string) => void;
  /** 지역 목록 전체 설정 (낙관적 갱신 + 서버 best-effort). */
  setRegions: (names: readonly string[]) => void;
  isSaving: boolean;
}

/**
 * 주로 가는 지역을 계정과 동기화하는 훅.
 * - 조회: 서버 우선, 성공 시 localStorage 캐시 갱신 / 실패(401·404·오프라인) 시 캐시 반환.
 * - 저장: localStorage 즉시 기록(낙관적) 후 서버 best-effort. 서버 실패해도 UI 유지.
 */
export function usePreferredRegions(): UsePreferredRegions {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: PREFERRED_REGIONS_QUERY_KEY,
    queryFn: async (): Promise<string[]> => {
      try {
        const remote = await getPreferredRegions();
        saveFavoriteRegions(remote);
        return remote;
      } catch {
        // 미인증/엔드포인트 부재/오프라인 — 로컬 캐시를 권위 소스로 사용
        return loadFavoriteRegions();
      }
    },
    initialData: () => loadFavoriteRegions(),
    staleTime: 5 * 60_000,
  });

  const mutation = useMutation({
    mutationFn: (names: readonly string[]): Promise<string[]> => {
      // 낙관적 캐시는 onMutate에서 이미 반영 — 여기선 서버 동기화만 시도
      return savePreferredRegions(names);
    },
    onMutate: (names: readonly string[]) => {
      const next = [...names];
      saveFavoriteRegions(next); // localStorage 즉시 기록
      const previous = qc.getQueryData<string[]>(PREFERRED_REGIONS_QUERY_KEY);
      qc.setQueryData<string[]>(PREFERRED_REGIONS_QUERY_KEY, next);
      return { previous };
    },
    onSuccess: (serverRegions) => {
      saveFavoriteRegions(serverRegions);
      qc.setQueryData<string[]>(PREFERRED_REGIONS_QUERY_KEY, serverRegions);
    },
    onError: () => {
      // 서버 저장 실패는 치명적이지 않음 — localStorage 낙관 값을 유지(롤백하지 않음)
    },
  });

  const regions: string[] = query.data ?? [];

  function setRegions(names: readonly string[]): void {
    mutation.mutate(names);
  }

  function toggle(name: string): void {
    const next = regions.includes(name)
      ? regions.filter((n) => n !== name)
      : [...regions, name];
    setRegions(next);
  }

  return {
    regions,
    toggle,
    setRegions,
    isSaving: mutation.isPending,
  };
}
